import styled from "styled-components";
import { colors } from "styles/color-tokens";
import HelpAndSupportIcon from "ui/header/icons/helpAndSupportIcon.svg";
import { openSupportChat } from "libs/support";
import { toast } from "react-toastify";
import { User } from "features/user";
import { PlanCurrency } from "./types";
import { HubSpotHelpChatTopic } from "libs/support/open-support-chat";

export const GenericErrorMessage = () => {
  return (
    <ErrorMainContainer>
      <ErrorHeader>Oops! Something went wrong.</ErrorHeader>
      <ErrorMessageContent>
        Please try again shortly, if the problem persists,{" "}
        <ContactSupport text="contact support" fontSize={20} /> for prompt
        assistance.
      </ErrorMessageContent>
    </ErrorMainContainer>
  );
};

export const ContactSupport = ({
  text = "Contact Support",
  fontSize,
  supportChatText,
  helpChatTopic,
}: {
  text?: string;
  fontSize?: number;
  supportChatText?: string;
  helpChatTopic?: HubSpotHelpChatTopic;
}) => {
  return (
    <ContactSupportText
      fontSize={fontSize}
      role="button"
      onClick={() => {
        openSupportChat(supportChatText || "", helpChatTopic);
      }}
    >
      {text} <HelpAndSupportIcon />
    </ContactSupportText>
  );
};

// NOTE: `symbol` here is the bare currency mark appended after `label`. For
// USD/AUD the country-name prefix comes from `label` (see PriceDisplay) when
// `includingCurrencyName` is truthy; when it's false the symbol alone must
// still unambiguously identify the currency — SKE-3250 fix below inlines
// `A$` for AUD when the label is suppressed.
export const CURRENCIES = [
  { label: "US", value: "usd", symbol: "$" },
  { label: "AU", value: "aud", symbol: "$" },
  { label: "EUR", value: "eur", symbol: "€" },
  { label: "GBP", value: "gbp", symbol: "£" },
] as const;

const isCurrency = (
  currency: string | undefined,
  ...compareTo: PlanCurrency[]
) => {
  return compareTo.some((cur) => cur.toLowerCase() === currency?.toLowerCase());
};

export const renderPriceWithSymbol = ({
  currency,
  showBilledPerYear,
  periodUnit = "month",
  price,
  includingCurrencyName = "us-au",
}: {
  currency: string;
  showBilledPerYear?: boolean;
  periodUnit?: string;
  price: string | number;
  includingCurrencyName?: boolean | "us-au";
}) => {
  const foundCur =
    CURRENCIES.find(
      (cur) => cur.value.toLowerCase() === currency?.toLowerCase()
    ) || CURRENCIES[0];
  const currencyName =
    includingCurrencyName === true ||
    (includingCurrencyName === "us-au" && isCurrency(currency, "USD", "AUD"))
      ? foundCur.label
      : "";
  // SKE-3250: AUD must always render unambiguously. When the country label is
  // suppressed we fall back to `A$` so AUD prices never display as a bare `$`.
  const effectiveSymbol =
    !currencyName && isCurrency(currency, "AUD") ? "A$" : foundCur.symbol;
  if (typeof price !== "string") {
    price = Number.isInteger(price)
      ? String(price)
      : Number.isNaN(price)
        ? price
        : price.toFixed(2);
  }

  if (periodUnit === "month") {
    return (
      <PriceDisplay
        currencyName={currencyName}
        symbol={effectiveSymbol}
        price={price}
      />
    );
  } else {
    if (showBilledPerYear) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      price = (Number.parseFloat(price as any) / 12).toFixed(2);
      return (
        <PriceDisplay
          currencyName={currencyName}
          symbol={effectiveSymbol}
          price={price}
        />
      );
    } else {
      return (
        <PriceDisplay
          currencyName={currencyName}
          symbol={effectiveSymbol}
          price={price}
        />
      );
    }
  }
};

const PriceDisplay = ({
  currencyName,
  symbol,
  price,
}: {
  currencyName: "US" | "AU" | "EUR" | "GBP" | "";
  symbol: string;
  price: string | number;
}) => (
  <>
    <span className="currency">
      {currencyName}
      {symbol}
    </span>
    <span className="price">{price}</span>
  </>
);

export const toastCollabUpsellError = (user: User) => {
  toast.error(
    `Please contact your admin ${user.adminEmail || ""}  to change your plan`
  );
};

const ErrorMainContainer = styled.div``;
const ErrorHeader = styled.div`
  font-family:
    Gotham SSm,
    Helvetica,
    Arial,
    sans-serif;
  font-size: 36px;
  font-weight: 400;
  line-height: 44px;
  color: ${colors.primary500};
`;
const ErrorMessageContent = styled.div`
  //styleName: Text xl/Regular;
  font-family: Inter;
  font-size: 20px;
  font-weight: 400;
  line-height: 30px;
`;

export const LinkText = styled.a<{ fontSize?: number }>`
  //styleName: Text sm/Medium;
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: ${({ fontSize }) => (fontSize ? `${fontSize}px` : "14px")};
  font-weight: 500;
  line-height: 20px;
  color: ${colors.primary500};
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:visited {
    color: ${colors.primary500};
  }

  &:hover {
    text-decoration: none;
  }
`;

const ContactSupportText = styled(LinkText)`
  > svg > path {
    stroke: ${colors.primary500};
  }
`;

export const UpsellHeader = styled.div`
  font-family: "Gotham SSm A", "Gotham SSm B", Helvetica, Arial, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: ${colors.primary900};
  margin-bottom: 4px;
`;
